import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { AppButton } from '../../components/AppButton';
import { AppDateField } from '../../components/AppDateField';
import { AppDialogFrame } from '../../components/AppDialogFrame';
import { AppInput } from '../../components/AppInput';
import { ProjectMemberPicker } from '../../components/ProjectMemberPicker';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { useTimeLogEmployee } from '../../hooks/useTimeLogEmployee';
import {
  addTaskTimeLog,
  moveProjectTask,
  updateProjectTask,
} from '../../services/projectService';
import type {
  ProjectBoardColumn,
  ProjectMember,
  ProjectTask,
} from '../../types/project';
import { projectErrorMessage } from '../../utils/projectErrors';
import {
  completedDateFieldError,
  resolveCompletedDate,
  type CompletedOnChoice,
} from '../../utils/taskCompletionDate';
import {
  formatDurationCompact,
  formatDurationInput,
  missingRequiredTaskDetails,
  validateCreateTaskForm,
  type TaskFormFieldErrors,
} from '../../utils/projectTaskForm';
import { todayBusinessCalendarDate } from '../../utils/dateTime';
import { runOperation } from '../../utils/operationFeedback';
import { timeRangeError } from '../../utils/taskTimeLog';
import { TimeEntryFields } from './TimeEntryFields';

const NO_ASSIGNEES: ProjectTask['assignees'] = [];

type Props = {
  visible: boolean;
  projectId: number;
  column: ProjectBoardColumn | null;
  members: ProjectMember[];
  task: ProjectTask | null;
  onClose: () => void;
  onCompleted: () => void;
};

export function CompleteTaskModal({
  visible,
  projectId,
  column,
  members,
  task,
  onClose,
  onCompleted,
}: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      title: { ...typography.sectionTitle, color: palette.textPrimary, flex: 1 },
      close: { ...typography.body, color: palette.textSecondary, fontWeight: '700' as const },
      hint: { ...typography.caption, color: palette.textSecondary },
      label: { ...typography.caption, color: palette.textSecondary, fontWeight: '600' as const },
      error: { ...typography.caption, color: palette.error },
    }),
    [palette],
  );

  const [assignedEmployeeIds, setAssignedEmployeeIds] = useState<number[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [timeEstimate, setTimeEstimate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [completedOn, setCompletedOn] = useState<CompletedOnChoice>('today');
  const [customCompletedDate, setCustomCompletedDate] = useState('');
  const [completedDateError, setCompletedDateError] = useState<string | undefined>();
  const [fieldErrors, setFieldErrors] = useState<TaskFormFieldErrors>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // The range already logged by this dialog, so a retry after a failed move
  // does not log the same time twice.
  const loggedRange = useRef<string | null>(null);
  const timeLogEmployee = useTimeLogEmployee({
    assignees: task?.assignees ?? NO_ASSIGNEES,
    members,
  });

  useEffect(() => {
    if (!visible || !task) return;
    setAssignedEmployeeIds(task.assignees.map(item => item.employeeId));
    setDueDate(task.dueDate ?? '');
    setTimeEstimate(formatDurationInput(task.timeEstimateMinutes));
    setStartTime('');
    setEndTime('');
    loggedRange.current = null;
    setCompletedOn('today');
    setCustomCompletedDate(todayBusinessCalendarDate());
    setCompletedDateError(undefined);
    setFieldErrors({});
    setError('');
    setBusy(false);
  }, [visible, task]);

  function close() {
    if (busy) return;
    onClose();
  }

  const missingAtOpen = task
    ? missingRequiredTaskDetails({
        assignedEmployeeIds: task.assignees.map(item => item.employeeId),
        assignedEmployeeId: task.assignedEmployeeId,
        dueDate: task.dueDate ?? '',
        timeEstimate: formatDurationInput(task.timeEstimateMinutes),
      })
    : [];
  const showMembers = missingAtOpen.includes('Member');
  const showDueDate = missingAtOpen.includes('Due Date');
  const alreadySpent = task?.timeSpentMinutes ?? 0;
  // A task with logged time may complete as is; otherwise one entry is needed.
  const requireTimeEntry = alreadySpent <= 0;

  async function complete() {
    if (busy || !column?.projectColumnId || !task) return;
    const result = validateCreateTaskForm({
      taskName: task.taskName,
      assignedEmployeeIds,
      dueDate,
      timeEstimate,
      timeSpent: formatDurationInput(alreadySpent) || '00:00',
      description: task.description ?? '',
      descriptionOpen: Boolean(task.description),
      subtasks: [],
      requiresTaskDetails: true,
      columnName: column.columnName,
    });
    const hasRange = Boolean(startTime.trim() || endTime.trim());
    const timeError =
      requireTimeEntry || hasRange
        ? timeRangeError(startTime, endTime) ?? timeLogEmployee.missingMessage ?? undefined
        : undefined;
    setFieldErrors({ ...result.fieldErrors, timeSpent: timeError });
    const dateError = completedDateFieldError(completedOn, customCompletedDate);
    setCompletedDateError(dateError);
    if (!result.payload || dateError || timeError) return;
    const completedDate = resolveCompletedDate(completedOn, customCompletedDate);
    const rangeKey = hasRange ? `${completedDate}|${startTime}|${endTime}` : null;
    setBusy(true);
    setError('');
    try {
      const payload = result.payload;
      const projectColumnId = column.projectColumnId;
      await runOperation('update', async () => {
        await updateProjectTask(projectId, task.taskId, {
          taskName: payload.taskName,
          assignedEmployeeIds: payload.assignedEmployeeIds ?? [],
          dueDate: payload.dueDate,
          completedDate,
          timeEstimate: payload.timeEstimate,
          // Unchanged here; the new entry below re-sums it on the server.
          timeSpent: payload.timeSpent,
          description: payload.description,
        });
        if (rangeKey && completedDate && loggedRange.current !== rangeKey) {
          await addTaskTimeLog(projectId, task.taskId, {
            date: completedDate,
            startTime,
            endTime,
            source: 'MANUAL',
            ...(timeLogEmployee.requestEmployeeId != null
              ? { employeeId: timeLogEmployee.requestEmployeeId }
              : {}),
          });
          loggedRange.current = rangeKey;
        }
        await moveProjectTask(projectId, task.taskId, projectColumnId, {
          completedDate,
        });
      });
      onCompleted();
    } catch (caught) {
      setError(projectErrorMessage(caught, 'Unable to complete the task.'));
    } finally {
      setBusy(false);
    }
  }

  if (!visible || !column || !task) return null;

  return (
    <AppDialogFrame visible onRequestClose={close}>
      <View style={[styles.flex, { backgroundColor: palette.background }]}>
        <View style={styles.header}>
          <Text style={themed.title}>Complete Task</Text>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close"
            onPress={close}
            hitSlop={8}
          >
            <Text style={themed.close}>X</Text>
          </Pressable>
        </View>
        <ScrollView
          contentContainerStyle={styles.body}
          keyboardShouldPersistTaps="handled"
        >
          <Text style={themed.hint}>
            Add a time estimate, the time you worked (From / To), and the date
            this task was completed to move “{task.taskName}” to {column.columnName}.
          </Text>

          <Text style={themed.label}>Completed on *</Text>
          <View style={styles.choiceRow}>
            {(
              [
                { value: 'yesterday', label: 'Yesterday' },
                { value: 'today', label: 'Today' },
                { value: 'custom', label: 'Other date' },
              ] as const
            ).map(choice => {
              const selected = completedOn === choice.value;
              return (
                <Pressable
                  key={choice.value}
                  accessibilityRole="button"
                  accessibilityLabel={choice.label}
                  accessibilityState={{ selected }}
                  onPress={() => {
                    setCompletedOn(choice.value);
                    setCompletedDateError(undefined);
                  }}
                  style={[
                    styles.choice,
                    {
                      borderColor: selected ? palette.primary : palette.border,
                      backgroundColor: selected
                        ? palette.primary
                        : palette.surface,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.choiceText,
                      {
                        color: selected ? palette.onPrimary : palette.textPrimary,
                      },
                    ]}
                  >
                    {choice.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
          {completedOn === 'custom' ? (
            <AppDateField
              label="Completion date *"
              value={customCompletedDate}
              onChange={value => {
                setCustomCompletedDate(value);
                setCompletedDateError(undefined);
              }}
              placeholder="Select date"
              error={completedDateError}
              maximumDate={new Date()}
            />
          ) : completedDateError ? (
            <Text accessibilityRole="alert" style={themed.error}>
              {completedDateError}
            </Text>
          ) : null}

          {showMembers ? (
            <ProjectMemberPicker
              label="Members *"
              members={members}
              selectedIds={assignedEmployeeIds}
              onChange={setAssignedEmployeeIds}
              error={fieldErrors.member}
            />
          ) : null}
          {showDueDate ? (
            <AppDateField
              label="Due Date *"
              value={dueDate}
              onChange={setDueDate}
              placeholder="Select date"
              error={fieldErrors.dueDate}
            />
          ) : null}
          <AppInput
            label="Time Estimate *"
            value={timeEstimate}
            onChangeText={setTimeEstimate}
            placeholder="00:00"
            error={fieldErrors.timeEstimate}
            autoCapitalize="none"
            keyboardType="numbers-and-punctuation"
          />
          {alreadySpent > 0 ? (
            <Text style={themed.hint}>
              Already logged: {formatDurationCompact(alreadySpent)}. Add another
              entry below if needed.
            </Text>
          ) : null}
          <TimeEntryFields
            startTime={startTime}
            endTime={endTime}
            onChangeStart={value => {
              setStartTime(value);
              setFieldErrors(current => ({ ...current, timeSpent: undefined }));
            }}
            onChangeEnd={value => {
              setEndTime(value);
              setFieldErrors(current => ({ ...current, timeSpent: undefined }));
            }}
            error={fieldErrors.timeSpent}
            required={requireTimeEntry}
            employee={timeLogEmployee}
          />

          {error ? (
            <Text accessibilityRole="alert" style={themed.error}>
              {error}
            </Text>
          ) : null}

          <View style={styles.actions}>
            <AppButton title="Cancel" variant="secondary" onPress={close} />
            <AppButton title="Complete" onPress={complete} loading={busy} />
          </View>
        </ScrollView>
      </View>
    </AppDialogFrame>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.xl,
    paddingBottom: spacing.md,
    gap: spacing.md,
  },
  body: {
    paddingHorizontal: spacing.xl,
    paddingBottom: spacing.xxl,
    gap: spacing.md,
  },
  choiceRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  choice: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  choiceText: {
    ...typography.caption,
    fontWeight: '700',
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
});
